// src/pages/PaymentPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import {
  ArrowLeft,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Lock,
} from 'lucide-react';

// ================= ВРАЩАЮЩАЯСЯ ЛАМА (с неоновой обводкой) =================
const SpinningLlama: React.FC<{ size?: number }> = ({ size = 100 }) => (
  <div
    style={{
      width: size,
      height: size,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
    }}
  >
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes neonPulse {
        0%, 100% { box-shadow: 0 0 15px #a855f7, 0 0 30px #7c3aed; }
        50% { box-shadow: 0 0 25px #c084fc, 0 0 50px #8b5cf6; }
      }
    `}</style>
    <div
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: '3px solid transparent',
        borderTopColor: '#a855f7',
        borderRightColor: '#7c3aed',
        borderBottomColor: '#a855f7',
        borderLeftColor: '#7c3aed',
        animation: 'spin 1.2s linear infinite, neonPulse 2s ease-in-out infinite',
        filter: 'drop-shadow(0 0 6px #a855f7)',
      }}
    />
    <img
      src="/images/load.png"
      alt="Лама"
      style={{
        width: size * 0.7,
        height: size * 0.7,
        objectFit: 'contain',
        zIndex: 1,
        filter: 'drop-shadow(0 0 12px rgba(168, 85, 247, 0.6))',
      }}
    />
  </div>
);

// ================= ПУРПУРНЫЕ РАЗМЫТЫЕ ОБЛАКА =================
const Cloud: React.FC<{
  top: number;
  left: number;
  scale: number;
  speed: number;
  delay: number;
}> = ({ top, left, scale, speed, delay }) => (
  <div
    style={{
      position: 'absolute',
      top: `${top}%`,
      left: `${left}%`,
      transform: `scale(${scale})`,
      animation: `cloudFloat ${speed}s ease-in-out ${delay}s infinite alternate`,
      zIndex: 0,
      pointerEvents: 'none',
      filter: 'blur(12px)',
      opacity: 0.3,
    }}
  >
    <svg
      width="140"
      height="80"
      viewBox="0 0 140 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ellipse cx="35" cy="55" rx="35" ry="22" fill="#c084fc" />
      <ellipse cx="70" cy="45" rx="45" ry="28" fill="#a855f7" />
      <ellipse cx="105" cy="55" rx="35" ry="22" fill="#c084fc" />
      <ellipse cx="55" cy="55" rx="55" ry="22" fill="#d8b4fe" />
      <ellipse cx="90" cy="60" rx="40" ry="18" fill="#e9d5ff" opacity="0.7" />
    </svg>
  </div>
);

// ================= ТАРИФЫ (синхронизированы с Landing) =================
type PlanPeriod = 'monthly' | 'quarterly' | 'yearly';

const PLANS: Record<
  PlanPeriod,
  {
    label: string;
    price: number;
    periodName: string;
    color: string;
    description: string;
    saving?: number;
    pricePerMonth?: number;
    features: string[];
  }
> = {
  monthly: {
    label: 'Помесячно',
    price: 2000,
    periodName: 'месяц',
    color: '#2575fc',
    description: '2 000 ₽ за 3 пользователей в месяц',
    features: [
      'Все модули платформы',
      'Расширение Chrome',
      'Ежедневная поддержка',
      'Обновления',
    ],
  },
  quarterly: {
    label: 'Квартал',
    price: 4500,
    periodName: 'квартал',
    color: '#28a745',
    description: '4 500 ₽ за 3 пользователей в квартал',
    features: [
      'Все модули платформы',
      'Расширение Chrome',
      'Ежедневная поддержка',
      'Обновления',
    ],
  },
  yearly: {
    label: 'Годовая',
    price: 15000,
    periodName: 'год',
    color: '#6a11cb',
    saving: 9000,
    pricePerMonth: 1250,
    description: '~1 250 ₽ за 3 пользователей в месяц',
    features: [
      'Все модули платформы',
      'Расширение Chrome',
      'Ежедневная поддержка',
      'Обновления',
    ],
  },
};

const PaymentPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedPlan, setSelectedPlan] = useState<PlanPeriod>('monthly');

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes cloudFloat {
        0% { transform: translateX(0); }
        100% { transform: translateX(50px); }
      }
      html, body, #root {
        margin: 0 !important;
        padding: 0 !important;
        height: 100% !important;
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const parts = [];
    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.substring(i, i + 4));
    }
    return parts.join(' ').slice(0, 19);
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + (v.length > 2 ? '/' + v.slice(2, 4) : '');
    }
    return v;
  };

  const handlePayment = async (method?: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/payment/create', {
        plan: selectedPlan,
        method: method || paymentMethod,
      });

      if (res.data.confirmation_url) {
        window.location.href = res.data.confirmation_url;
      } else {
        setError('Не удалось получить ссылку на оплату');
        setLoading(false);
      }
    } catch (err: any) {
      const details = err.response?.data?.details || '';
      const errorMsg = err.response?.data?.error || '';
      let userMessage = 'Ошибка при создании платежа. Пожалуйста, попробуйте позже.';
      if (details.includes('Payment method is not available')) {
        userMessage = 'Этот способ оплаты временно недоступен. Пожалуйста, выберите другой способ.';
      } else if (details || errorMsg) {
        userMessage = details || errorMsg;
      }
      setError(userMessage);
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (user.paid) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />
        <Cloud top={10} left={5} scale={1.2} speed={12} delay={0} />
        <Cloud top={70} left={75} scale={0.8} speed={10} delay={2.5} />

        <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>На главную</span>
        </button>

        <div style={styles.card}>
          <div style={styles.successBlock}>
            <CheckCircle size={64} color="#28a745" />
            <h2 style={styles.successTitle}>Подписка активна</h2>
            <p style={styles.successText}>
              У вас уже оплачен доступ ко всем функциям системы.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={styles.returnButton}
            >
              Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    );
  }

  const plan = PLANS[selectedPlan];

  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />
      <Cloud top={15} left={5} scale={1} speed={14} delay={0} />
      <Cloud top={60} left={80} scale={1.3} speed={11} delay={1} />
      <Cloud top={85} left={20} scale={0.9} speed={13} delay={3} />

      <button
        onClick={() => navigate('/dashboard')}
        style={styles.backButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderColor = 'transparent';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
          e.currentTarget.style.color = '#2575fc';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
        }}
      >
        <ArrowLeft size={18} />
        <span>На главную</span>
      </button>

      <div style={styles.card}>
        {success ? (
          <div style={styles.successBlock}>
            <CheckCircle size={64} color="#28a745" />
            <h2 style={styles.successTitle}>Заявка принята!</h2>
            <p style={styles.successText}>
              Ваша заявка на оплату тарифа «{plan.label}» принята. Статус
              изменится на «В оплате». Администратор подтвердит платёж в
              ближайшее время.
            </p>
            <p style={styles.redirectText}>
              Вы будете перенаправлены на главную страницу через несколько
              секунд...
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <SpinningLlama size={80} />
            </div>

            <div style={styles.header}>
              <Lock size={24} color="#2575fc" />
              <h2 style={styles.title}>Оплата подписки</h2>
            </div>

            <div style={styles.infoBox}>
              <CreditCard size={20} color="#2575fc" style={{ marginRight: 8 }} />
              <span>Безопасное соединение • Данные карты не сохраняются</span>
            </div>

            <p style={styles.description}>
              Выберите тарифный период и оплатите доступ к платформе. Цена
              указана за 3 пользователей.
            </p>

            <div style={styles.planSelector}>
              {(Object.keys(PLANS) as PlanPeriod[]).map((key) => {
                const p = PLANS[key];
                const isActive = selectedPlan === key;
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedPlan(key)}
                    style={{
                      ...styles.planOption,
                      borderColor: isActive ? p.color : 'rgba(255,255,255,0.4)',
                      backgroundColor: isActive
                        ? `${p.color}20`
                        : 'rgba(255,255,255,0.3)',
                      boxShadow: isActive
                        ? `0 0 20px ${p.color}, 0 8px 25px rgba(0,0,0,0.1)`
                        : 'none',
                      transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    <div style={styles.planLabel}>{p.label}</div>
                    <div style={{ ...styles.planPrice, color: p.color }}>
                      {p.price.toLocaleString()} ₽
                    </div>
                    <div style={styles.planPeriod}>/ {p.periodName}</div>
                    {key === 'yearly' && (
                      <div style={styles.planSaving}>
                        Экономия {p.saving!.toLocaleString()} ₽
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={styles.selectedPlanInfo}>
              <div style={styles.planDescription}>{plan.description}</div>
              <ul style={styles.featuresList}>
                {plan.features.map((feat, i) => (
                  <li key={i}>
                    <CheckCircle size={14} color={plan.color} style={{ marginRight: 6 }} />
                    {feat}
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <div style={styles.errorBox}>
                <AlertCircle size={18} color="#ff4d4f" style={{ marginRight: 8 }} />
                <span>{error}</span>
              </div>
            )}

            <div style={styles.methodsGrid}>
              {[
                { id: 'yoomoney', label: 'ЮMoney',           icon: '/payment-icons/ukassa.png' },
                { id: 'card',     label: 'Банковские карты', emoji: '💳' },
                { id: 'sberpay',  label: 'SberPay',          icon: '/payment-icons/sber.svg' },
                { id: 'sbp',      label: 'СБП',              icon: '/payment-icons/sbp.png' },
                { id: 'tpay',     label: 'T-Pay',            icon: '/payment-icons/tbank.png' },
                { id: 'alfapay', label: 'Alfa Pay',         icon: '/payment-icons/alfa.png' },
              ].map((m) => (
                <div
                  key={m.id}
                  onClick={() => setPaymentMethod(m.id)}
                  style={{
                    ...styles.methodOption,
                    borderColor: paymentMethod === m.id ? plan.color : 'rgba(255,255,255,0.4)',
                    backgroundColor: paymentMethod === m.id ? `${plan.color}20` : 'rgba(255,255,255,0.3)',
                    boxShadow: paymentMethod === m.id ? `0 0 20px ${plan.color}` : 'none',
                  }}
                >
                  {'icon' in m && m.icon ? (
                    <img
                      src={m.icon as string}
                      alt={m.label}
                      style={styles.methodIcon}
                    />
                  ) : (
                    <div style={styles.methodEmoji}>{(m as any).emoji}</div>
                  )}
                  <div style={styles.methodLabel}>{m.label}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => handlePayment()}
              disabled={loading}
              style={styles.payButton}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 12px 24px rgba(40,167,69,0.5), 0 0 20px rgba(34,197,94,0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 8px 16px rgba(40,167,69,0.3), 0 0 10px rgba(34,197,94,0.2)';
              }}
            >
              <CreditCard size={18} style={{ marginRight: 8 }} />
              {loading ? 'Обработка...' : `Оплатить ${plan.price.toLocaleString()} ₽`}
            </button>

            <p style={styles.hint}>
              После нажатия «Оплатить» вы будете перенаправлены на защищённую страницу
              ЮKassa. Оплата проходит на стороне платёжной системы.
            </p>

            <div style={styles.securityInfo}>
              <Lock size={14} color="#666" style={{ marginRight: 4 }} />
              <span>Данные защищены по стандарту PCI DSS</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ================= СТИЛИ (насыщенные тени и неон) =================
const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #f8fafd 0%, #ede9fe 100%)', // лёгкий пурпурный оттенок
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  bgBlur1: {
    position: 'absolute',
    top: '-10%',
    left: '-5%',
    width: '600px',
    height: '600px',
    background:
      'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute',
    bottom: '-10%',
    right: '-5%',
    width: '700px',
    height: '700px',
    background:
      'radial-gradient(circle, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  backButton: {
    position: 'absolute',
    top: 24,
    left: 24,
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 40,
    padding: '10px 20px',
    fontSize: 15,
    fontWeight: 500,
    color: '#2575fc',
    cursor: 'pointer',
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    transition: 'all 0.2s ease',
    zIndex: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  card: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(18px)',
    borderRadius: 32,
    padding: 40,
    width: '100%',
    maxWidth: 520,
    boxShadow:
      '0 30px 45px rgba(0,0,0,0.12), 0 0 30px rgba(168,85,247,0.15), 0 0 60px rgba(124,58,237,0.1)',
    border: '1px solid rgba(255,255,255,0.6)',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  title: {
  fontSize: 28,
  fontWeight: 700,
  color: '#7c3aed', // фиолетовый
  textShadow: '0 0 10px rgba(168,85,247,0.2)',
  margin: 0,
},
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(124,58,237,0.1)',
    borderRadius: 40,
    padding: '10px 16px',
    marginBottom: 20,
    fontSize: 13,
    color: '#7c3aed',
    border: '1px solid rgba(124,58,237,0.25)',
  },
  description: {
    fontSize: 14,
    color: '#4a4a68',
    lineHeight: 1.6,
    marginBottom: 24,
    textAlign: 'center',
  },
  planSelector: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  planOption: {
    flex: '1 1 130px',
    minWidth: 120,
    borderRadius: 20,
    padding: '16px 12px',
    textAlign: 'center',
    border: '2px solid transparent',
    backdropFilter: 'blur(8px)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  planLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 2,
  },
  planPrice: {
    fontSize: 22,
    fontWeight: 800,
  },
  planPeriod: {
    fontSize: 12,
    color: '#888',
  },
  planSaving: {
    marginTop: 4,
    background: 'rgba(168,85,247,0.2)',
    color: '#7c3aed',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  methodsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
    marginBottom: 20,
  },
  methodOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '14px 10px',
    borderRadius: 16,
    border: '2px solid transparent',
    backdropFilter: 'blur(8px)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    gap: 6,
  },
  methodEmoji: {
    fontSize: 24,
  },

  methodIcon: {
    width: 40,
    height: 40,
    objectFit: 'contain' as const,
  },
  methodLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a1a2e',
    textAlign: 'center',
  },
  selectedPlanInfo: {
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: 20,
    padding: 16,
    marginBottom: 24,
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
  },
  planDescription: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1a1a2e',
    marginBottom: 10,
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 13,
    color: '#4a4a68',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(255,77,79,0.12)',
    borderRadius: 40,
    padding: '10px 16px',
    marginBottom: 20,
    fontSize: 13,
    color: '#ff4d4f',
    border: '1px solid rgba(255,77,79,0.25)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#4a4a68',
  },
  input: {
    padding: '14px 16px',
    borderRadius: 30,
    border: '1px solid rgba(255,255,255,0.6)',
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(8px)',
    fontSize: 15,
    outline: 'none',
    transition: 'border 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  },
  rowInputs: {
    display: 'flex',
    gap: 12,
  },
  payButton: {
    background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '16px 24px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 12,
    boxShadow:
      '0 8px 25px rgba(124,58,237,0.4), 0 0 20px rgba(168,85,247,0.5)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hint: {
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 16,
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#666',
    gap: 4,
  },
  successBlock: {
    textAlign: 'center',
    padding: '20px 0',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a2e',
    marginTop: 20,
    marginBottom: 12,
  },
  successText: {
    fontSize: 15,
    color: '#4a4a68',
    lineHeight: 1.6,
    marginBottom: 24,
  },
  redirectText: {
    fontSize: 13,
    color: '#888',
    marginTop: 16,
  },
  returnButton: {
    background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 8px 25px rgba(124,58,237,0.4)',
  },
};

export default PaymentPage;
